import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Rocket } from "lucide-react";
import { PACKAGES } from "@/data";
import { useStore } from "@/context/StoreContext";

export const Calculator = () => {
  const { setBookingTier } = useStore();
  const [guests, setGuests] = useState(45);
  const [hours, setHours] = useState(2);
  const [tubs, setTubs] = useState(0);

  const recommended = useMemo(
    () => PACKAGES.find((p) => p.duration_hours >= hours && p.max_people >= guests) || null,
    [guests, hours]
  );
  const total = (recommended?.price || 0) + tubs * 10;

  return (
    <section id="calculator" data-testid="calculator-section" className="relative py-24 sm:py-32">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
          className="glass relative overflow-hidden rounded-3xl p-8 sm:p-12">
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-cosmic-purple/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-cosmic-cyan/15 blur-3xl" />

          <div className="relative">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-cosmic-yellow">Event Estimator</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
              Size up your <span className="text-gradient-candy">mission</span>
            </h2>

            <div className="mt-10 grid gap-10 md:grid-cols-2">
              <div className="space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-slate-200">Guests</label>
                    <span data-testid="calc-guests-value" className="font-display text-2xl font-black text-cosmic-cyan">{guests}</span>
                  </div>
                  <input data-testid="calc-guests-input" type="range" min="10" max="150" step="5" value={guests}
                    onChange={(e) => setGuests(Number(e.target.value))}
                    className="w-full accent-cosmic-pink" />
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-200 block mb-3">Hours of live spinning</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4].map((h) => (
                      <button key={h} data-testid={`calc-hours-${h}-button`} onClick={() => setHours(h)}
                        className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-colors duration-300 ${
                          hours === h ? "bg-white text-cosmic-base" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
                        {h} hr{h > 1 ? "s" : ""}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-200 block mb-3">Bulk premium tubs ($10 each)</label>
                  <div className="flex items-center gap-4">
                    <button data-testid="calc-tubs-minus" onClick={() => setTubs((t) => Math.max(0, t - 1))}
                      className="rounded-full glass p-2.5 text-white hover:border-cosmic-pink/50" aria-label="Remove tub">
                      <Minus size={16} />
                    </button>
                    <span data-testid="calc-tubs-value" className="font-display text-2xl font-black text-white w-8 text-center">{tubs}</span>
                    <button data-testid="calc-tubs-plus" onClick={() => setTubs((t) => Math.min(50, t + 1))}
                      className="rounded-full glass p-2.5 text-white hover:border-cosmic-pink/50" aria-label="Add tub">
                      <Plus size={16} />
                    </button>
                    <span className="text-xs text-slate-400">Jolly Rancher series, Spicy Tamarindo, Zkittlez…</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-center rounded-2xl bg-cosmic-base/60 border border-white/10 p-7">
                {recommended ? (
                  <>
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Recommended package</span>
                    <div data-testid="calc-package-name" className="font-display mt-2 text-2xl font-black text-white">{recommended.name}</div>
                    <div className="mt-1 text-sm text-slate-400">
                      {recommended.duration_hours} hr · up to {recommended.max_people} guests · {recommended.flavor_count} flavors · unlimited cones
                    </div>
                    <div className="mt-5 space-y-1.5 text-sm text-slate-300">
                      <div className="flex justify-between"><span>Package</span><span>${recommended.price}</span></div>
                      {tubs > 0 && <div className="flex justify-between"><span>Bulk premium tubs × {tubs}</span><span>${tubs * 10}</span></div>}
                    </div>
                    <div className="mt-4 flex items-end justify-between border-t border-white/10 pt-4">
                      <span className="text-sm font-semibold text-slate-300">Estimated total</span>
                      <span data-testid="calc-total" className="font-display text-4xl font-black text-gradient-candy">${total}</span>
                    </div>
                    <button data-testid="calc-book-button" onClick={() => setBookingTier(recommended)}
                      className="mt-6 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple py-3.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.03] glow-pink">
                      <Rocket size={16} /> Request This Booking
                    </button>
                  </>
                ) : (
                  <p data-testid="calc-custom-note" className="text-slate-300 text-sm leading-relaxed">
                    Your mission is bigger than our standard orbit (120+ guests). Send us a booking request with any
                    package and tell us your headcount in the notes — we will build a custom quote.
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Calculator;
